// elementAnimationUtils: Zod-backed parsing & comparison helpers for element-scoped animations
import { LogManager } from '@src/utility/Logger';
import { z } from 'zod';

const logger = LogManager.getLogger('utility:elementAnimationKeyframes');

// Minimal permissive schema (fields optional; passthrough preserves forward-compatible extensions)
export const ElementAnimationKeyframeSchema = z
  .object({
    time: z.union([z.string(), z.number()]).optional(),
    type: z.string().optional(),
    arbor_subType: z.string().optional(),
    easing: z.string().optional(),
    scope: z.string().optional(),
    start_scale: z.string().optional(),
    end_scale: z.string().optional(),
    start_x: z.string().optional(),
    start_y: z.string().optional(),
    end_x: z.string().optional(),
    end_y: z.string().optional(),
    fade: z.boolean().optional(),
  })
  .passthrough();

export type ArborElementAnimationKeyframe = z.infer<
  typeof ElementAnimationKeyframeSchema
>;

export function parseElementAnimationKeyframes(
  raw: unknown,
): ArborElementAnimationKeyframe[] {
  if (!Array.isArray(raw)) return [];
  const parsed: ArborElementAnimationKeyframe[] = [];
  let warned = false;
  for (const item of raw) {
    const result = ElementAnimationKeyframeSchema.safeParse(item);
    if (result.success) {
      parsed.push(result.data);
    } else if (!warned) {
      warned = true;
      logger.warn('Invalid element animation keyframe(s) encountered', {
        issues: result.error.issues.slice(0, 5),
      });
    }
  }
  return parsed;
}

export function elementAnimationEquals(
  a: ArborElementAnimationKeyframe[],
  b: ArborElementAnimationKeyframe[],
) {
  if (a.length !== b.length) return false;
  const sortKey = (k: ArborElementAnimationKeyframe) =>
    `${k.arbor_subType || ''}:${k.type || ''}:${k.time ?? ''}`;
  const sa = [...a].sort((x, y) => sortKey(x).localeCompare(sortKey(y)));
  const sb = [...b].sort((x, y) => sortKey(x).localeCompare(sortKey(y)));
  for (let i = 0; i < sa.length; i++) {
    const x = sa[i];
    const y = sb[i];
    if (
      x.time !== y.time ||
      x.type !== y.type ||
      x.arbor_subType !== y.arbor_subType ||
      x.start_scale !== y.start_scale ||
      x.end_scale !== y.end_scale ||
      x.start_x !== y.start_x ||
      x.start_y !== y.start_y ||
      x.end_x !== y.end_x ||
      x.end_y !== y.end_y ||
      !!x.fade !== !!y.fade
    )
      return false;
  }
  return true;
}
